import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Clock, Check, Gift, Flame } from "lucide-react";
import { PUBLIC_ORIGIN } from "../lib/domain";

const GOLD = "#C9A961";
const RED = "#D90429";
const LOGO = `${PUBLIC_ORIGIN}/logos/institut_impact/iiem_master_wordmark_disciplines_transparent.png`;
const DEADLINE = "2026-09-08T23:59:59"; // fin de la promo de rentrée

const AVANTAGES = [
  "25 % de rabais sur tes frais d'inscription au dojo",
  "Cours d'essai 19,99 $ · sans engagement",
  "Encadrement premium : Muay Thaï, Kickboxing, Jiu-jitsu (BJJ) & Can-Ryu",
  "Carnet de progression numérique dès le 1er cours",
  "Communauté motivante pour toute la famille",
];

function useCountdown(target) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0, over: false });
  useEffect(() => {
    const tick = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0, over: true });
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
        over: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}

export default function RentreePromoPublic() {
  const c = useCountdown(DEADLINE);
  const Box = ({ v, l }) => (
    <div className="flex flex-col items-center">
      <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-xl bg-black/60 border border-white/15 flex items-center justify-center text-3xl sm:text-4xl font-black" style={{ color: GOLD }}>{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-2">{l}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fafafa]" data-testid="rentree-promo">
      <header className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 80% 15%, ${RED}26 0%, transparent 45%), radial-gradient(circle at 10% 90%, ${GOLD}1f 0%, transparent 40%)` }} />
        <div className="relative max-w-5xl mx-auto px-6 py-14">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8" data-testid="rentree-back"><ArrowLeft size={15} /> Accueil</Link>
          <img src={LOGO} alt="Institut Impact Élite Martial" className="h-12 mb-6" onError={(e) => { e.target.style.display = "none"; }} />
          <div className="chip mb-5 inline-flex items-center gap-2 border border-white/20 px-3 py-1 text-xs uppercase tracking-[0.15em]" style={{ color: GOLD }}><Gift size={13} /> Promo de rentrée · Back to School</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-3xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Fais ta <span style={{ color: RED }}>rentrée</span> sur le tatami
          </h1>
          <p className="mt-4 text-lg text-white/75 max-w-2xl">Nouvelle saison, nouvelle discipline. Rejoins l'Institut Impact Élite Martial et commence fort — <b className="text-white">25 % de rabais sur tes frais d'inscription</b> pour la rentrée.</p>

          {/* Compte à rebours */}
          <div className="mt-8" data-testid="rentree-countdown">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3 flex items-center gap-1.5"><Clock size={13} style={{ color: GOLD }} /> {c.over ? "Promo terminée" : "Fin de la promo dans"}</div>
            {!c.over && (
              <div className="flex gap-3 sm:gap-4">
                <Box v={c.d} l="jours" /><Box v={c.h} l="heures" /><Box v={c.m} l="min" /><Box v={c.s} l="sec" />
              </div>
            )}
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/inscription?promo=rentree" className="bg-[#D90429] text-white px-7 py-3.5 font-bold uppercase tracking-wide text-sm hover:bg-white hover:text-[#D90429] transition-colors" data-testid="rentree-cta-inscription">Je m'inscris maintenant</Link>
            <Link to="/cours-essai" className="border border-white/25 px-7 py-3.5 font-bold uppercase tracking-wide text-sm hover:border-[var(--g)] hover:text-[var(--g)] transition-colors" style={{ "--g": GOLD }} data-testid="rentree-cta-essai">Cours d'essai 19,99 $</Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="flex items-center gap-2 mb-4"><Sparkles size={18} style={{ color: GOLD }} /><span className="kz-mono text-[11px] tracking-[0.3em] uppercase" style={{ color: GOLD }}>Pourquoi maintenant</span></div>
            <h2 className="text-3xl font-black mb-5" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Ce que tu obtiens</h2>
            <ul className="space-y-3">
              {AVANTAGES.map((a, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80" data-testid={`rentree-avantage-${i}`}>
                  <Check size={18} className="mt-0.5 shrink-0" style={{ color: GOLD }} /><span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-[#C9A961]/40 bg-gradient-to-br from-[#151006] to-black p-8 text-center">
            <Flame size={28} style={{ color: GOLD }} className="mx-auto mb-3" />
            <div className="text-5xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>-25 %</div>
            <div className="text-white/60 text-sm mt-1">sur tes frais d'inscription</div>
            <div className="text-white/40 text-xs mt-1">cours en groupe à l'Institut Impact · offre de rentrée</div>
            <Link to="/inscription?promo=rentree" className="mt-6 inline-block bg-[var(--g)] text-black px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-white transition-colors" style={{ "--g": GOLD }} data-testid="rentree-cta-promo">Profiter de l'offre</Link>
            <p className="text-white/40 text-xs mt-4">Places limitées — offre valable jusqu'au 8 septembre 2026.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
